/**
 * Notification Service
 * Handles browser notification permissions, service worker registration,
 * and API calls for notification preferences
 */

import { callExternalApi } from "./external-api.service";
import { getAccessTokenSilentlyOutsideComponent } from "./workspace.service";

const API_SERVER_URL = import.meta.env.VITE_API_SERVER_URL;

/**
 * Check if browser supports notifications
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
  return "Notification" in globalThis && "serviceWorker" in navigator;
};

/**
 * Get current notification permission status
 * @returns {'granted' | 'denied' | 'default'}
 */
export const getPermissionStatus = () => {
  if (!isNotificationSupported()) {
    return "denied";
  }
  return Notification.permission;
};

/**
 * Request notification permission from the browser
 * @returns {Promise<'granted' | 'denied' | 'default'>}
 */
export const requestPermission = async () => {
  if (!isNotificationSupported()) {
    console.warn("[NotificationService] Notifications not supported");
    return "denied";
  }

  try {
    const permission = await Notification.requestPermission();
    console.log("[NotificationService] Permission result:", permission);
    return permission;
  } catch (error) {
    console.error("[NotificationService] Permission request failed:", error);
    return "denied";
  }
};

/**
 * Register the push service worker
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
export const registerServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) {
    console.warn("[NotificationService] Service workers not supported");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register("/sw-push.js", {
      scope: "/",
    });
    console.log(
      "[NotificationService] Service worker registered:",
      registration.scope,
    );
    return registration;
  } catch (error) {
    console.error(
      "[NotificationService] Service worker registration failed:",
      error,
    );
    return null;
  }
};

/**
 * Get the service worker registration
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
export const getServiceWorkerRegistration = async () => {
  if (!("serviceWorker" in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.error("[NotificationService] Failed to get service worker:", error);
    return null;
  }
};

// ==================== API CALLS ====================

/**
 * Get notification prompt status from the server
 * Determines if we should show the opt-in popup
 * @returns {Promise<{data: Object | null, error: Object | null}>}
 */
export const getNotificationPromptStatus = async () => {
  console.log("[NotificationService] Getting notification prompt status");

  const config = {
    url: `${API_SERVER_URL}/api/users/notification-prompt-status`,
    method: "GET",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${await getAccessTokenSilentlyOutsideComponent()}`,
    },
  };

  const { data, error } = await callExternalApi({ config });

  if (error) {
    console.error("[NotificationService] Failed to get prompt status:", error);
  }

  return { data, error };
};

/**
 * Update notification preferences on the server
 * @param {boolean} enabled - Whether notifications are enabled
 * @returns {Promise<{data: Object | null, error: Object | null}>}
 */
export const updateNotificationPreferences = async (enabled) => {
  console.log(
    "[NotificationService] Updating notification preferences:",
    enabled,
  );

  const config = {
    url: `${API_SERVER_URL}/api/users/notification-preferences`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${await getAccessTokenSilentlyOutsideComponent()}`,
    },
    data: {
      browserNotificationsEnabled: enabled,
    },
  };

  const { data, error } = await callExternalApi({ config });

  if (error) {
    console.error("[NotificationService] Failed to update preferences:", error);
  }

  return { data, error };
};

/**
 * Record that the user dismissed the notification prompt
 * @param {boolean} dontAskAgain - If true, won't ask again
 * @returns {Promise<{data: Object | null, error: Object | null}>}
 */
export const recordPromptDismissed = async (dontAskAgain = false) => {
  console.log(
    "[NotificationService] Recording prompt dismissal, dontAskAgain:",
    dontAskAgain,
  );

  const config = {
    url: `${API_SERVER_URL}/api/users/notification-prompt-dismissed`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${await getAccessTokenSilentlyOutsideComponent()}`,
    },
    data: {
      dontAskAgain,
    },
  };

  const { data, error } = await callExternalApi({ config });

  if (error) {
    console.error("[NotificationService] Failed to record dismissal:", error);
  }

  return { data, error };
};

// ==================== LOCAL STORAGE ====================

const DEVICE_ID_KEY = "audioscribe_notification_device_id";

/**
 * Get or create a unique device ID for this browser
 * Used for Ably push registration
 * @returns {string}
 */
export const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    console.log("[NotificationService] Generated new device ID:", deviceId);
  }

  return deviceId;
};

/**
 * Clear the device ID (used when disabling notifications)
 */
export const clearDeviceId = () => {
  localStorage.removeItem(DEVICE_ID_KEY);
  console.log("[NotificationService] Cleared device ID");
};

// ==================== TEST UTILITIES ====================

/**
 * Show a test notification (for development/testing)
 * @param {string} title - Notification title
 * @param {string} body - Notification body
 */
export const showTestNotification = async (title, body) => {
  if (!isNotificationSupported()) {
    console.warn("[NotificationService] Notifications not supported");
    return;
  }

  if (Notification.permission !== "granted") {
    console.warn("[NotificationService] Notifications not permitted");
    return;
  }

  const registration = await getServiceWorkerRegistration();
  if (registration) {
    // Use service worker to show notification (works in background)
    await registration.showNotification(title, {
      body,
      icon: "/icon-192.png",
      badge: "/badge-72.png",
      tag: "test-notification",
    });
  } else {
    // Fallback to regular notification (only works in foreground)
    new Notification(title, {
      body,
      icon: "/icon-192.png",
    });
  }
};
